import React, { useState, useRef } from 'react';
import { router } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Upload, X, File as FileIcon, CheckCircle, Loader2, FolderIcon } from 'lucide-react';

interface FileUploadProps {
    folderId?: number;
    onClose: () => void;
}

interface FileWithStatus {
    file: File;
    relativePath: string;
    status: 'pending' | 'uploading' | 'success' | 'error';
    error?: string;
}

export default function FileUpload({ folderId, onClose }: FileUploadProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [files, setFiles] = useState<FileWithStatus[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const items = Array.from(e.dataTransfer.items);
        handleDataTransferItems(items);
    };

    const handleDataTransferItems = async (items: DataTransferItem[]) => {
        const allFiles: FileWithStatus[] = [];

        for (const item of items) {
            if (item.kind === 'file') {
                const entry = item.webkitGetAsEntry();
                if (entry) {
                    const filesFromEntry = await getAllFilesFromEntry(entry, '');
                    allFiles.push(...filesFromEntry);
                }
            }
        }

        setFiles(prev => [...prev, ...allFiles]);
    };

    const getAllFilesFromEntry = async (entry: any, path: string): Promise<FileWithStatus[]> => {
        const files: FileWithStatus[] = [];

        if (entry.isFile) {
            const file = await new Promise<File>((resolve) => {
                entry.file((f: File) => resolve(f));
            });

            files.push({
                file,
                relativePath: path ? `${path}/${file.name}` : file.name,
                status: 'pending'
            });
        } else if (entry.isDirectory) {
            const reader = entry.createReader();
            const entries = await new Promise<any[]>((resolve) => {
                reader.readEntries((e: any[]) => resolve(e));
            });

            for (const childEntry of entries) {
                const childPath = path ? `${path}/${entry.name}` : entry.name;
                const childFiles = await getAllFilesFromEntry(childEntry, childPath);
                files.push(...childFiles);
            }
        }

        return files;
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        if (selectedFiles) {
            const filesArray = Array.from(selectedFiles);
            const filesWithStatus: FileWithStatus[] = filesArray.map(file => ({
                file,
                relativePath: (file as any).webkitRelativePath || file.name,
                status: 'pending'
            }));
            setFiles(prev => [...prev, ...filesWithStatus]);
        }
    };

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    // const uploadFiles = async () => {
    //     setIsUploading(true);

    //     try {
    //         // POPRAWKA 1: Użyj current state value, nie starej wartości z closure
    //         const filesToUpload = files.filter(f => f.status === 'pending');

    //         if (filesToUpload.length === 0) {
    //             setIsUploading(false);
    //             return;
    //         }

    //         // 1. Wyodrębnij unikalne ścieżki folderów
    //         const folderPaths = new Set<string>();
    //         filesToUpload.forEach(f => {
    //             const pathParts = f.relativePath.split('/');
    //             if (pathParts.length > 1) {
    //                 for (let i = 1; i < pathParts.length; i++) {
    //                     const folderPath = pathParts.slice(0, i).join('/');
    //                     folderPaths.add(folderPath);
    //                 }
    //             }
    //         });

    //         // 2. Utwórz foldery jeśli są potrzebne
    //         let folderMap: Record<string, number> = {};
    //         if (folderPaths.size > 0) {
    //             const response = await fetch('/folders/bulk_create', {
    //                 method: 'POST',
    //                 headers: {
    //                     'Content-Type': 'application/json',
    //                     'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    //                 },
    //                 body: JSON.stringify({
    //                     folder_paths: Array.from(folderPaths),
    //                     parent_id: folderId
    //                 })
    //             });

    //             const data = await response.json();
    //             folderMap = data.folder_map;

    //             console.log('Created folders:', folderMap);
    //         }

    //         // 3. Ustaw wszystkie na uploading
    //         setFiles(prev => prev.map(f =>
    //             f.status === 'pending' ? { ...f, status: 'uploading' } : f
    //         ));

    //         // POPRAWKA 2: Użyj filesToUpload zamiast files z closure
    //         const uploadPromises = filesToUpload.map((fileWithStatus, originalIndex) => {
    //             // Znajdź indeks w oryginalnej tablicy files
    //             const index = files.findIndex(f => f.file === fileWithStatus.file);

    //             const formData = new FormData();
    //             formData.append('uploaded_file[file]', fileWithStatus.file);
    //             formData.append('uploaded_file[name]', fileWithStatus.file.name);

    //             // Znajdź folder_id dla tego pliku
    //             const pathParts = fileWithStatus.relativePath.split('/');
    //             let targetFolderId = folderId;

    //             if (pathParts.length > 1) {
    //                 const folderPath = pathParts.slice(0, -1).join('/');
    //                 targetFolderId = folderMap[folderPath] || folderId;
    //                 console.log(`File ${fileWithStatus.file.name} -> folder path: ${folderPath} -> folder_id: ${targetFolderId}`);
    //             }

    //             if (targetFolderId) {
    //                 formData.append('uploaded_file[folder_id]', targetFolderId.toString());
    //             }

    //             return new Promise<void>((resolve, reject) => {
    //                 router.post('/uploaded_files', formData, {
    //                     preserveState: true,
    //                     preserveScroll: true,
    //                     onSuccess: () => {
    //                         setFiles(prev => prev.map((f, idx) =>
    //                             idx === index ? { ...f, status: 'success' } : f
    //                         ));
    //                         resolve();
    //                     },
    //                     onError: (errors) => {
    //                         console.error('Upload error:', errors);
    //                         setFiles(prev => prev.map((f, idx) =>
    //                             idx === index ? {
    //                                 ...f,
    //                                 status: 'error',
    //                                 error: errors.file?.toString() || 'Upload failed'
    //                             } : f
    //                         ));
    //                         reject(errors);
    //                     }
    //                 });
    //             });
    //         });

    //         await Promise.allSettled(uploadPromises);
    //     } catch (error) {
    //         console.error('Upload error:', error);
    //     }

    //     setIsUploading(false);

    //     const allSuccess = files.every(f =>
    //         f.status === 'success' || f.status === 'pending'
    //     );

    //     if (allSuccess) {
    //         setTimeout(() => {
    //             router.reload();
    //             onClose();
    //         }, 500);
    //     }
    // };

    const uploadFiles = async () => {
        setIsUploading(true);

        try {
            const filesToUpload = files.filter(f => f.status === 'pending');

            if (filesToUpload.length === 0) {
                setIsUploading(false);
                return;
            }

            // 1. Wyodrębnij unikalne ścieżki folderów
            const folderPaths = new Set<string>();
            filesToUpload.forEach(f => {
                const pathParts = f.relativePath.split('/');
                if (pathParts.length > 1) {
                    for (let i = 1; i < pathParts.length; i++) {
                        const folderPath = pathParts.slice(0, i).join('/');
                        folderPaths.add(folderPath);
                    }
                }
            });

            // 2. Utwórz foldery jeśli są potrzebne
            let folderMap: Record<string, number> = {};
            if (folderPaths.size > 0) {
                const response = await fetch('/folders/bulk_create', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                    },
                    body: JSON.stringify({
                        folder_paths: Array.from(folderPaths),
                        parent_id: folderId
                    })
                });

                const data = await response.json();
                folderMap = data.folder_map;

                console.log('Created folders:', folderMap);
            }

            // 3. Ustaw wszystkie na uploading
            setFiles(prev => prev.map(f =>
                f.status === 'pending' ? { ...f, status: 'uploading' } : f
            ));

            // 4. POPRAWKA: Użyj fetch() zamiast router.post()
            const uploadPromises = filesToUpload.map(async (fileWithStatus, idx) => {
                const index = files.findIndex(f => f.file === fileWithStatus.file);

                const formData = new FormData();
                formData.append('uploaded_file[file]', fileWithStatus.file);
                formData.append('uploaded_file[name]', fileWithStatus.file.name);

                // Znajdź folder_id dla tego pliku
                const pathParts = fileWithStatus.relativePath.split('/');
                let targetFolderId = folderId;

                if (pathParts.length > 1) {
                    const folderPath = pathParts.slice(0, -1).join('/');
                    targetFolderId = folderMap[folderPath] || folderId;
                    console.log(`File ${fileWithStatus.file.name} -> folder: ${folderPath} -> id: ${targetFolderId}`);
                }

                if (targetFolderId) {
                    formData.append('uploaded_file[folder_id]', targetFolderId.toString());
                }

                try {
                    const response = await fetch('/uploaded_files', {
                        method: 'POST',
                        headers: {
                            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                            'Accept': 'application/json',
                        },
                        body: formData,
                    });

                    if (response.ok) {
                        setFiles(prev => prev.map((f, i) =>
                            i === index ? { ...f, status: 'success' } : f
                        ));
                        console.log(`✓ Uploaded: ${fileWithStatus.file.name}`);
                    } else {
                        const errorData = await response.json().catch(() => ({}));
                        setFiles(prev => prev.map((f, i) =>
                            i === index ? {
                                ...f,
                                status: 'error',
                                error: errorData.error || 'Upload failed'
                            } : f
                        ));
                        console.error(`✗ Failed: ${fileWithStatus.file.name}`, errorData);
                    }
                } catch (error) {
                    setFiles(prev => prev.map((f, i) =>
                        i === index ? {
                            ...f,
                            status: 'error',
                            error: 'Network error'
                        } : f
                    ));
                    console.error(`✗ Error: ${fileWithStatus.file.name}`, error);
                }
            });

            await Promise.allSettled(uploadPromises);

            console.log('All uploads completed');
        } catch (error) {
            console.error('Upload error:', error);
        }

        setIsUploading(false);

        // Sprawdź czy wszystkie się udały
        const allSuccess = files.every(f =>
            f.status === 'success' || f.status === 'pending'
        );

        if (allSuccess) {
            setTimeout(() => {
                router.reload();
                onClose();
            }, 500);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getStatusIcon = (status: FileWithStatus['status']) => {
        switch (status) {
            case 'uploading':
                return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
            case 'success':
                return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'error':
                return <X className="h-5 w-5 text-red-500" />;
            default:
                return <FileIcon className="h-5 w-5 text-muted-foreground" />;
        }
    };

    const canUpload = files.length > 0 && !isUploading;
    const hasErrors = files.some(f => f.status === 'error');

    // Grupuj pliki po folderach dla wyświetlenia
    const filesByFolder: Record<string, FileWithStatus[]> = {};
    files.forEach(f => {
        const pathParts = f.relativePath.split('/');
        const folderPath = pathParts.length > 1 ? pathParts.slice(0, -1).join('/') : 'root';
        if (!filesByFolder[folderPath]) {
            filesByFolder[folderPath] = [];
        }
        filesByFolder[folderPath].push(f);
    });

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Upload Files</DialogTitle>
                    <DialogDescription>
                        Drag & drop multiple folders or use buttons below
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4">
                    {files.length === 0 ? (
                        <div className="space-y-4">
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                className={`
                  border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                  transition-colors
                  ${isDragging
                                        ? 'border-primary bg-primary/5'
                                        : 'border-muted-foreground/25 hover:border-primary/50'
                                    }
                `}
                            >
                                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                                <p className="text-sm font-medium mb-1">
                                    Drop files or folders here
                                </p>
                                <p className="text-xs text-muted-foreground mb-4">
                                    You can drop multiple folders at once
                                </p>
                                <div className="flex gap-2 justify-center">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <FileIcon className="h-4 w-4 mr-2" />
                                        Select Files
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => folderInputRef.current?.click()}
                                    >
                                        <FolderIcon className="h-4 w-4 mr-2" />
                                        Select Folder
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Note: Button can select only one folder. Drag & drop supports multiple folders.
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={handleFileInputChange}
                                />
                                <input
                                    ref={folderInputRef}
                                    type="file"
                                    {...{ webkitdirectory: "", directory: "" } as any}
                                    className="hidden"
                                    onChange={handleFileInputChange}
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => !isUploading && fileInputRef.current?.click()}
                                className={`
                  border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
                  transition-colors
                  ${isDragging
                                        ? 'border-primary bg-primary/5'
                                        : 'border-muted-foreground/25 hover:border-primary/50'
                                    }
                  ${isUploading ? 'opacity-50 pointer-events-none' : ''}
                `}
                            >
                                <p className="text-sm text-muted-foreground">
                                    Drop more files/folders or click to add
                                </p>
                            </div>

                            <div className="space-y-4 max-h-96 overflow-y-auto">
                                {Object.entries(filesByFolder).map(([folderPath, folderFiles]) => (
                                    <div key={folderPath} className="space-y-2">
                                        {folderPath !== 'root' && (
                                            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                                <FolderIcon className="h-4 w-4" />
                                                {folderPath}
                                            </div>
                                        )}
                                        <div className="space-y-2 pl-6">
                                            {folderFiles.map((fileWithStatus, index) => {
                                                const globalIndex = files.indexOf(fileWithStatus);
                                                return (
                                                    <div
                                                        key={`${folderPath}-${index}`}
                                                        className="border rounded-lg p-3 flex items-center justify-between gap-3"
                                                    >
                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                            {getStatusIcon(fileWithStatus.status)}
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-sm truncate">
                                                                    {fileWithStatus.file.name}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {formatFileSize(fileWithStatus.file.size)}
                                                                    {fileWithStatus.error && (
                                                                        <span className="text-red-500 ml-2">
                                                                            - {fileWithStatus.error}
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {fileWithStatus.status === 'pending' && !isUploading && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => removeFile(globalIndex)}
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                        {files.length} file{files.length !== 1 ? 's' : ''} selected
                        {Object.keys(filesByFolder).length > 1 &&
                            ` in ${Object.keys(filesByFolder).length - (filesByFolder['root'] ? 1 : 0)} folder${Object.keys(filesByFolder).length !== 2 ? 's' : ''}`
                        }
                    </p>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isUploading}
                        >
                            {hasErrors ? 'Close' : 'Cancel'}
                        </Button>
                        <Button
                            onClick={uploadFiles}
                            disabled={!canUpload}
                        >
                            {isUploading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Uploading...
                                </>
                            ) : (
                                `Upload ${files.length} file${files.length !== 1 ? 's' : ''}`
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}